import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users } from "lucide-react";
import { handwritten } from "./editorial";

/**
 * HappeningsStrip — live preview band beneath the Hero.
 *
 * Currently stubbed with sample data for the localhost preview. Phase 2
 * follow-up wires this to the same Supabase RPC the report bot reads
 * (`events_attendance_summary`) so the band reflects real upcoming
 * Happenings.
 *
 * The Polaroid pattern + ± rotation matches the rest of the editorial
 * design system. Each card links into /events/[slug] so the existing
 * conversion path stays intact.
 */
const sample = [
  {
    slug: "hackney-walk",
    title: "Hackney Sunday walk",
    when: "Sun 9am",
    where: "Hackney Marshes",
    loopedIn: 12,
    emoji: "🚶",
    rotate: "-rotate-2",
    bg: "var(--color-paper-salmon)",
  },
  {
    slug: "battersea-volleyball",
    title: "Battersea volleyball",
    when: "Tue 6:30pm",
    where: "Battersea Park",
    loopedIn: 8,
    emoji: "🏐",
    rotate: "rotate-1",
    bg: "var(--color-paper-mint)",
  },
  {
    slug: "borough-pizza",
    title: "Pizza after work",
    when: "Fri 7pm",
    where: "Borough Market",
    loopedIn: 6,
    emoji: "🍕",
    rotate: "-rotate-1",
    bg: "var(--color-paper-tan)",
  },
  {
    slug: "covent-coffee",
    title: "Coffee at Monmouth",
    when: "Wed 10am",
    where: "Covent Garden",
    loopedIn: 4,
    emoji: "☕",
    rotate: "rotate-2",
    bg: "var(--color-paper-salmon)",
  },
  {
    slug: "regents-run",
    title: "Sunday morning run",
    when: "Sun 9am",
    where: "Regent's Park",
    loopedIn: 14,
    emoji: "🏃",
    rotate: "-rotate-1",
    bg: "var(--color-paper-mint)",
  },
  {
    slug: "shoreditch-book-club",
    title: "Book club tonight",
    when: "Thu 7:30pm",
    where: "Shoreditch",
    loopedIn: 9,
    emoji: "📚",
    rotate: "rotate-1",
    bg: "var(--color-paper-tan)",
  },
];

export default function HappeningsStrip() {
  return (
    <section
      id="happenings-strip"
      className="w-full py-20 md:py-28 px-6 md:px-12 overflow-hidden"
      style={{ backgroundColor: "var(--color-paper)" }}
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between mb-10 max-w-5xl"
        >
          <div>
            <div className="flex items-center gap-4 mb-3">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
                01: Happening now
              </span>
              <span className="flex-1 h-px bg-ink/15" />
            </div>
            <p
              className="text-2xl md:text-3xl text-salmon -rotate-1 mb-1"
              style={handwritten}
            >
              this week, near you
            </p>
            <h2 className="text-3xl md:text-5xl font-serif tracking-tight leading-[1.05]">
              Real plans.{" "}
              <span className="italic text-salmon">Real people.</span>
            </h2>
          </div>
          <Link
            to="/download"
            className="hidden md:inline-flex items-center gap-1.5 text-ink/70 font-semibold border-b-2 border-ink/20 hover:border-salmon hover:text-salmon transition-colors text-sm"
          >
            See all in the app
            <ArrowRight size={14} />
          </Link>
        </motion.div>

        {/* Horizontal scroll strip */}
        <div className="relative -mx-6 md:-mx-12 px-6 md:px-12">
          <div className="flex gap-6 overflow-x-auto py-10 snap-x snap-mandatory scrollbar-none">
            {sample.map((h, i) => (
              <motion.div
                key={h.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="snap-start shrink-0 w-[280px] md:w-[300px]"
              >
                <Link to={`/events/${h.slug}`} className="block group">
                  <div
                    className={`polaroid-frame p-3 pb-8 border shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] hover:shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] transition-shadow transform-gpu ${h.rotate} group-hover:rotate-0`}
                    style={{ transition: "transform 0.3s ease" }}
                  >
                    <div
                      className="aspect-[4/5] w-full flex items-center justify-center text-7xl"
                      style={{ backgroundColor: h.bg }}
                    >
                      {h.emoji}
                    </div>
                    <p
                      className="mt-3 text-center text-xl text-ink leading-tight px-2"
                      style={handwritten}
                    >
                      {h.title}
                    </p>
                    <div className="mt-3 flex items-center justify-between px-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin size={11} />
                        {h.where}
                      </span>
                      <span>{h.when}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between px-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-salmon">
                        Loop in &rarr;
                      </span>
                      <span className="flex items-center gap-1 text-xs font-semibold text-ink">
                        <Users size={11} />
                        {h.loopedIn}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="text-center md:hidden mt-4">
          <Link
            to="/download"
            className="inline-flex items-center gap-1.5 text-ink/70 font-semibold border-b-2 border-ink/20 hover:border-salmon hover:text-salmon transition-colors text-sm"
          >
            See loops in the app
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
